import { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthLayout from '@/components/common/AuthLayout';
import NewPasswordForm from '@/components/auth/NewPasswordForm';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { updatePassword } from '@/features/auth/authThunks';
import { Button } from '@/components/ui/button';

export default function NewPasswordPage() {
  const { t } = useTranslation('auth');
  const dispatch = useAppDispatch();
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  const handleSubmit = useCallback(
    async (data: { password: string }) => {
      if (!oobCode) {
        setFormError(t('errors.invalidActionCode'));
        return;
      }
      setFormError(null);
      const result = await dispatch(updatePassword({ oobCode, password: data.password }));
      if (updatePassword.fulfilled.match(result)) {
        setSuccess(true);
      } else if (updatePassword.rejected.match(result)) {
        const messageKey = result.payload?.message ?? 'errors.generic';
        setFormError(t(messageKey));
      }
    },
    [dispatch, oobCode, t],
  );

  const title = success ? t('passwordUpdatedTitle') : t('newPasswordTitle');

  return (
    <AuthLayout title={title} background="gradient" success={success}>
      {!success ? (
        <NewPasswordForm onSubmit={handleSubmit} loading={false} formError={formError} />
      ) : (
        <div className="space-y-16">
          <p className="text-sm text-secondary-inverse">{t('logoutAllSessionsInfo')}</p>
          <Button asChild className="w-full h-11 rounded-full text-base">
            <Link to="/login">{t('login')}</Link>
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
